import { useCallback, useEffect, useMemo, useState } from 'react';
import { Swipe, usePageMeta, cx } from '../ui';

// Review-only photography mood board (route /__moodboard). Reads /images/v2/gen/manifest.json.
type Item = {
  id: string;
  src: string;
  thumb: string;
  w: number;
  h: number;
  library: 'ownership' | 'operations' | 'indigo';
  sector: string;
  caption: string;
  prompt: string;
  source: 'generated' | 'brand-guidelines';
};
type Pick = 'keep' | 'maybe' | 'reject';
// Generated frames don't always keep the left third calm; these read better with type on the right.
const CALM_RIGHT = new Set(['agri-handover', 'lifting-inspection', 'kitchen-service', 'fitness-service', 'resale-collect', 'dealer-counter']);

const LIBRARIES: Record<Item['library'], { name: string; note: string }> = {
  ownership: { name: 'Ownership moments', note: 'People living with products at home and outdoors. Website, campaigns.' },
  operations: { name: 'Brand operations', note: 'Factories, dealers, service benches, field engineers. B2B pages, case studies, blog.' },
  indigo: { name: 'Product on indigo', note: 'Still life on #241E61 with a lime rim light. Heroes, social, decks.' },
};

const RULES_DO = [
  'Real light, real work, one clear subject',
  'Warm natural grade, soft contrast, present day',
  'Calm left third for type',
  'Hands and tools over faces staring at products',
  'Unbranded machinery, nothing readable in frame',
  'Placed in the 5px gradient frame, or full bleed with an indigo scrim',
];
const RULES_DONT = [
  'Glowing screens, holograms, robot arms, neon, particles',
  'Handshakes, pointing at laptops, desk dashboards',
  'Real brands, logos, CE marks or readable text',
  'Generated people presented as customers or staff',
  'Tilts, offsets, drop shadows, boxes around photos',
  'Sepia or cold blue grades',
];

const STORAGE = 'bm-moodboard-picks';
const HEADLINES = [
  ['Know who owns every', 'machine', 'you have ever sold.'],
  ['The warranty starts at', 'handover.', ''],
  ['Your dealers stay in', 'every sale.', ''],
  ['Every unit you have built,', 'accounted for.', ''],
];

function loadPicks(): Record<string, Pick> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE) || '{}');
  } catch {
    return {};
  }
}

export default function Moodboard() {
  usePageMeta('Photography mood board', 'Review-only: BrandedMark photography direction.');
  const [items, setItems] = useState<Item[]>([]);
  const [generatedAt, setGeneratedAt] = useState('');
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [withType, setWithType] = useState(true);
  const [filter, setFilter] = useState<'all' | Item['library'] | 'generated' | 'keep'>('all');
  const [open, setOpen] = useState<Item | null>(null);
  const [picks, setPicks] = useState<Record<string, Pick>>(loadPicks);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch('/images/v2/gen/manifest.json', { cache: 'no-store' })
      .then((r) => r.json())
      .then((m) => {
        setItems(m.items);
        setGeneratedAt(m.generatedAt);
      })
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE, JSON.stringify(picks));
    } catch {
      // storage unavailable
    }
  }, [picks]);

  const reel = useMemo(() => items.filter((x) => picks[x.id] !== 'reject'), [items, picks]);
  const current = reel.length ? reel[i % reel.length] : null;
  const next = useCallback(() => setI((n) => (reel.length ? (n + 1) % reel.length : 0)), [reel.length]);
  const prev = useCallback(() => setI((n) => (reel.length ? (n - 1 + reel.length) % reel.length : 0)), [reel.length]);

  useEffect(() => {
    if (!playing || reel.length < 2) return;
    const t = window.setTimeout(next, 3800);
    return () => window.clearTimeout(t);
  }, [playing, i, reel.length, next]);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (open) {
        if (e.key === 'Escape') setOpen(null);
        return;
      }
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === ' ' && (e.target as HTMLElement)?.tagName !== 'BUTTON') {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [next, prev, open]);

  const shown = items.filter((x) =>
    filter === 'all' ? true : filter === 'generated' ? x.source === 'generated' : filter === 'keep' ? picks[x.id] === 'keep' : x.library === filter
  );

  const setPick = (id: string, p: Pick) => setPicks((all) => ({ ...all, [id]: all[id] === p ? (undefined as unknown as Pick) : p }));

  const exportPicks = async () => {
    const lines = (['keep', 'maybe', 'reject'] as Pick[]).map(
      (p) => `${p.toUpperCase()}: ${items.filter((x) => picks[x.id] === p).map((x) => x.id).join(', ') || '(none)'}`
    );
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable
    }
  };

  const headline = HEADLINES[i % HEADLINES.length];
  const right = !!current && CALM_RIGHT.has(current.id);
  const counts = { keep: 0, maybe: 0, reject: 0 } as Record<Pick, number>;
  Object.values(picks).forEach((p) => p && (counts[p] += 1));

  return (
    <div className="mb-root on-dark" style={{ background: '#181343' }}>
      <style>{`
        .mb-root .mb-slide { animation: mb-drift 4.4s var(--bm-ease) both; }
        @keyframes mb-drift { from { transform: scale(1.06) translateX(1.2%); } to { transform: scale(1.0) translateX(0); } }
        .mb-root .mb-tick { transition: background-color .2s var(--bm-ease); }
        .mb-root .mb-tile img { transition: transform .5s var(--bm-ease); }
        .mb-root .mb-tile:hover img { transform: scale(1.03); }
        .mb-root .mb-stage { height: 125vw; max-height: 78vh; }
        @media (min-width: 768px) { .mb-root .mb-stage { height: min(78vh, 56.25vw); min-height: 340px; } }
        @media (prefers-reduced-motion: reduce) { .mb-root .mb-slide { animation: none; } }
      `}</style>

      {/* Header */}
      <section className="wrap pb-6 pt-8 md:pt-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <div className="t-eyebrow">Review · photography direction</div>
            <h1 className="t-h2 mt-2">
              How BrandedMark <Swipe draw>looks</Swipe>.
            </h1>
            <p className="mt-3 max-w-2xl text-[15.5px] text-white/70">
              Editorial photographs of the product's world. Real light, real work, one clear subject, and a calm side for type. Pick what feels like us; reject what doesn't.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[13px] text-white/70">
            <span className="chip" style={{ background: 'rgba(255,255,255,.06)', color: '#fff', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.14)' }}>
              {items.length} images
            </span>
            <span>
              Keep {counts.keep} · Maybe {counts.maybe} · Reject {counts.reject}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={exportPicks}>
              {copied ? 'Copied' : 'Copy picks'}
            </button>
          </div>
        </div>
      </section>

      {/* Reel */}
      <section aria-label="Photography reel" className="relative">
        <div className="mb-stage relative mx-auto overflow-hidden" style={{ maxWidth: 1760 }}>
          {current && (
            <img
              key={current.id + i}
              src={current.src}
              alt={current.caption}
              className="mb-slide absolute inset-0 h-full w-full object-cover"
            />
          )}
          {/* indigo scrim on the calm left side */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: `linear-gradient(${right ? 270 : 90}deg, rgba(24,19,67,.84) 0%, rgba(24,19,67,.5) 34%, rgba(24,19,67,0) 62%)` }}
          />
          {withType && (
            <div className={cx('absolute inset-y-0 flex max-w-[600px] flex-col justify-center px-6 md:px-14', right ? 'right-0' : 'left-0')}>
              <div className="t-eyebrow hidden md:block">The ownership layer for physical products</div>
              <h2 className="t-h1 md:mt-4" style={{ fontSize: 'clamp(30px, 4.6vw, 64px)' }}>
                {headline[0]} <Swipe>{headline[1]}</Swipe> {headline[2]}
              </h2>
            </div>
          )}
          {current && (
            <div className="mb-controls flex flex-wrap items-end justify-between gap-3 px-6 pb-5 pt-4 md:absolute md:bottom-0 md:left-0 md:right-0 md:px-14 md:pt-0">
              <div className="text-[13px] text-white/80">
                <span className="font-mono uppercase tracking-[.12em] text-[#CFF710]">{LIBRARIES[current.library].name}</span>
                <span className="mx-2 text-white/40">/</span>
                {current.sector} · {current.caption}
              </div>
              <div className="flex items-center gap-2">
                <button className="btn btn-ghost btn-sm" onClick={prev} aria-label="Previous image">
                  ←
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setPlaying((p) => !p)}>
                  {playing ? 'Pause' : 'Play'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={next} aria-label="Next image">
                  →
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setWithType((t) => !t)}>
                  {withType ? 'Hide type' : 'Show type'}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* progress ticks */}
        <div className="wrap mt-4 flex gap-1.5" aria-hidden="true">
          {reel.map((x, n) => (
            <button
              key={x.id}
              onClick={() => setI(n)}
              className="mb-tick h-1 flex-1 rounded-full"
              style={{ background: n === i % Math.max(reel.length, 1) ? 'linear-gradient(95deg,#3FAE6C,#8FD53A 55%,#CFF710)' : 'rgba(255,255,255,.16)' }}
              tabIndex={-1}
            />
          ))}
        </div>
      </section>

      {/* Rules */}
      <section className="wrap grid gap-10 py-16 md:grid-cols-3">
        <div>
          <div className="t-eyebrow">The recipe</div>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            Brief it like a photographer: subject and materials, a real setting, camera and lens, the light, where the calm space is, one mood word. Always warm, natural and present-day, with nothing readable in frame.
          </p>
          <div className="mt-6 grid grid-cols-5 overflow-hidden rounded-xl" aria-label="Palette">
            {['#241E61', '#181343', '#3FAE6C', '#8FD53A', '#CFF710'].map((c) => (
              <div key={c} style={{ background: c, height: 44 }} title={c} />
            ))}
          </div>
          <p className="mt-2 text-[12px] text-white/50">Indigo or lime should appear in every frame, through the scrim, frame or props.</p>
        </div>
        <div>
          <div className="t-eyebrow">Do</div>
          <ul className="mt-4 space-y-2.5 text-[15px] text-white/85">
            {RULES_DO.map((r) => (
              <li key={r} className="flex gap-3">
                <span className="mt-[9px] h-1.5 w-1.5 flex-none rounded-full bg-[#8FD53A]" />
                {r}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="t-eyebrow" style={{ color: '#ffb4a8' }}>
            Don't
          </div>
          <ul className="mt-4 space-y-2.5 text-[15px] text-white/70">
            {RULES_DONT.map((r) => (
              <li key={r} className="flex gap-3">
                <span className="mt-[9px] h-1.5 w-1.5 flex-none rounded-full bg-white/30" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Board */}
      <section className="pb-24" style={{ background: '#F7F7FC' }}>
        <div className="wrap pt-14">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="t-h2" style={{ color: '#241E61' }}>
              The board
            </h2>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['all', 'All'],
                  ['ownership', LIBRARIES.ownership.name],
                  ['operations', LIBRARIES.operations.name],
                  ['indigo', LIBRARIES.indigo.name],
                  ['generated', 'New this round'],
                  ['keep', 'My keeps'],
                ] as const
              ).map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={cx('rounded-full px-4 py-2 text-[13.5px] font-medium transition-colors', filter === k ? 'bg-[#241E61] text-white' : 'bg-white text-[#241E61]')}
                  style={{ boxShadow: filter === k ? 'none' : 'inset 0 0 0 1px #E4E3F0' }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          {filter in LIBRARIES && <p className="mt-3 text-[15px] text-[#67648F]">{LIBRARIES[filter as Item['library']].note}</p>}

          <div className="mt-8 columns-1 gap-5 sm:columns-2 lg:columns-3">
            {shown.map((x) => {
              const p = picks[x.id];
              return (
                <figure key={x.id} className="mb-5 break-inside-avoid">
                  <div className={cx(p === 'keep' ? 'frame' : '')} style={p === 'keep' ? undefined : { padding: 5 }}>
                    <button
                      className="mb-tile block w-full overflow-hidden"
                      style={{ borderRadius: 17, opacity: p === 'reject' ? 0.35 : 1 }}
                      onClick={() => setOpen(x)}
                      aria-label={`Open ${x.caption}`}
                    >
                      <img src={x.thumb} alt={x.caption} width={x.w} height={x.h} loading="lazy" className="block h-auto w-full" />
                    </button>
                  </div>
                  <figcaption className="flex items-start justify-between gap-3 px-1.5 pt-2.5">
                    <div>
                      <div className="text-[14px] font-semibold text-[#241E61]">{x.caption}</div>
                      <div className="text-[12.5px] text-[#67648F]">
                        {x.sector}
                        {x.source === 'generated' ? ' · new' : ' · brand library'}
                      </div>
                    </div>
                    <div className="flex flex-none gap-1" role="group" aria-label="Your pick">
                      {(['keep', 'maybe', 'reject'] as Pick[]).map((k) => (
                        <button
                          key={k}
                          onClick={() => setPick(x.id, k)}
                          aria-pressed={p === k}
                          className="rounded-full px-2.5 py-1 text-[12px] font-medium"
                          style={{
                            background: p === k ? (k === 'keep' ? '#2F9E58' : k === 'maybe' ? '#241E61' : '#b42318') : '#fff',
                            color: p === k ? '#fff' : '#67648F',
                            boxShadow: p === k ? 'none' : 'inset 0 0 0 1px #E4E3F0',
                          }}
                        >
                          {k === 'keep' ? 'Keep' : k === 'maybe' ? 'Maybe' : 'No'}
                        </button>
                      ))}
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
          {generatedAt && <p className="mt-6 text-[12.5px] text-[#67648F]">Manifest updated {generatedAt.replace('T', ' ')}. New images are generated from the brief recipe and reviewed against the rules above before use.</p>}
        </div>
      </section>

      {/* Lightbox */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={open.caption}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10"
          style={{ background: 'rgba(15,12,46,.92)' }}
          onClick={() => setOpen(null)}
        >
          <div className="w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="frame">
              <img src={open.src} alt={open.caption} className="block h-auto w-full" />
            </div>
            <div className="mt-4 flex flex-wrap items-start justify-between gap-4 text-white">
              <div className="max-w-3xl">
                <div className="text-[16px] font-semibold">{open.caption}</div>
                <div className="text-[13px] text-white/60">
                  {LIBRARIES[open.library].name} · {open.sector}
                </div>
                {open.prompt && <p className="mt-3 font-mono text-[12px] leading-relaxed text-white/55">{open.prompt}</p>}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
